import { type CSSProperties, type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Button } from '@/components/atoms/Buttons';

import type { SaveBlocker } from '../hooks/saveBlocker';
import { usePinnedFooter } from '../hooks/usePinnedFooter';

import styles from './ProviderCard.module.scss';

const BLOCKER_KEY: Readonly<Record<SaveBlocker, string>> = {
    nothingChanged: 'providerConfig.save.nothingChanged',
    invalid: 'providerConfig.errors.invalid',
    unfinished: 'providerConfig.save.unfinishedRow',
    requiredMissing: 'providerConfig.save.fillRequired',
    testRequired: 'providerConfig.test.required',
};

/** Scrolls to and focuses the first marked field in `container`, after a refused save. */
export function scrollToFirstProblem(container: HTMLElement | null): void {
    const target = container?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (!target) return;
    if (typeof target.scrollIntoView === 'function')
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
}

export interface CardFooterProps {
    blocker: SaveBlocker | null;
    saving: boolean;
    onSave: () => void;
    /** The card the bar belongs to: it pins only while this is on screen. */
    cardRef: RefObject<HTMLElement | null>;
}

type PinnedStyle = CSSProperties & { '--pinned-left'?: string; '--pinned-width'?: string };

/**
 * The card's Save button and, while Save is disabled, why. The bar is one element: in flow at
 * the card's end, or pinned to the viewport bottom across the card while that end is out of
 * view, so there is one Save at a time and focus stays on it when the bar moves. The slot
 * keeps the bar's height meanwhile, so the card does not jump.
 */
function CardFooter({ blocker, saving, onSave, cardRef }: CardFooterProps) {
    const { t } = useTranslation();
    const slotRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);
    const pinned = usePinnedFooter(cardRef, slotRef, height);

    // After every in-flow render, so a blocker note that adds a line is measured too; an
    // unchanged height bails out of the update.
    useLayoutEffect(() => {
        const bar = barRef.current;
        if (pinned !== null || bar === null) return;
        setHeight((previous) => (previous === bar.offsetHeight ? previous : bar.offsetHeight));
    });

    const style: PinnedStyle | undefined = pinned
        ? { '--pinned-left': `${pinned.left}px`, '--pinned-width': `${pinned.width}px` }
        : undefined;
    return (
        <div
            ref={slotRef}
            className={styles.footerSlot}
            style={pinned ? { minHeight: height } : undefined}>
            <div
                ref={barRef}
                className={classNames(styles.footer, { [styles.pinned]: pinned !== null })}
                style={style}
                data-pinned={pinned !== null || undefined}>
                {blocker !== null && <p className={styles.note}>{t(BLOCKER_KEY[blocker])}</p>}
                <Button variant="primary" disabled={blocker !== null || saving} onClick={onSave}>
                    {t('providerConfig.save.button')}
                </Button>
            </div>
        </div>
    );
}

export default CardFooter;
export { CardFooter };
